import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, MenuItem, Select, FormControl, InputLabel,
    Paper, Typography, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, Divider, Grid, FormControlLabel, Switch, InputAdornment, TablePagination
} from "@mui/material";
import { Search, Edit, Delete, ReceiptLong, CalendarMonth } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";

function Monthlypayment() {
    const [staffList, setStaffList] = useState([]);
    const [payments, setPayments] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [attendance, setAttendance] = useState([]);

    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    const [page, setPage] = useState(0);
    // Set default rows per page to 10
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [form, setForm] = useState({
        staffId: "",
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        baseSalary: 0,
        attendanceEarnings: 0,
        advanceDeduction: 0,
        shouldDeductAdvance: true,
        netPaid: 0,
        payDate: new Date().toLocaleDateString('en-CA'),
        method: "Cash"
    });

    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const years = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const staffSnap = await getDocs(collection(db, "staff"));
            // Ordering by payDate desc ensures the latest/last entries display first
            const paySnap = await getDocs(query(collection(db, "monthlypayments"), orderBy("payDate", "desc")));
            const advSnap = await getDocs(collection(db, "staffadvances"));
            const attSnap = await getDocs(collection(db, "staffattendance"));

            setStaffList(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setPayments(paySnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAdvances(advSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAttendance(attSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            toast.error("Failed to sync with database");
        }
    };

    const handleOpen = (data = null) => {
        if (data) {
            setEditMode(true);
            setCurrentId(data.id);
            setForm({ ...data });
        } else {
            setEditMode(false);
            setForm({
                staffId: "", month: new Date().getMonth() + 1, year: new Date().getFullYear(),
                baseSalary: 0, attendanceEarnings: 0, advanceDeduction: 0,
                shouldDeductAdvance: true, netPaid: 0,
                payDate: new Date().toLocaleDateString('en-CA'), method: "Cash"
            });
        }
        setOpen(true);
    };

    const calculateLogic = (sId, m, y, deduct) => {
        const staff = staffList.find(s => s.id === sId);
        if (!staff) return;

        let absentCount = 0;
        let halfDayCount = 0;

        attendance.forEach(att => {
            const d = new Date(att.date);
            if (
                att.staffId === sId &&
                d.getMonth() + 1 === Number(m) &&
                d.getFullYear() === Number(y)
            ) {
                if (att.status === "Absent") absentCount++;
                if (att.status === "Half Day") halfDayCount++;
            }
        });

        const daysInMonth = new Date(Number(y), Number(m), 0).getDate();
        const baseSalary = Number(staff.payment || 0);
        const dailyRate = baseSalary / daysInMonth;

        const deductibleAbsentDays = absentCount > 3 ? (absentCount - 3) : 0;
        const totalDaysToDeduct = deductibleAbsentDays + (halfDayCount * 0.5);

        const earned = Math.max(0, Math.round(baseSalary - (totalDaysToDeduct * dailyRate)));

        // Advance logic restricted to the selected month and year
        const totalAdv = advances
            .filter(a => {
                if (a.staffId !== sId) return false;
                const d = new Date(a.date);
                return d.getMonth() + 1 === Number(m) && d.getFullYear() === Number(y);
            })
            .reduce((sum, item) => sum + Number(item.payment || 0), 0);

        const deductionAmount = deduct ? totalAdv : 0;

        setForm(prev => ({
            ...prev,
            staffId: sId,
            month: Number(m),
            year: Number(y),
            baseSalary: baseSalary,
            attendanceEarnings: earned,
            advanceDeduction: deductionAmount,
            shouldDeductAdvance: deduct,
            netPaid: Math.max(0, earned - deductionAmount)
        }));
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this payment record?")) {
            try {
                await deleteDoc(doc(db, "monthlypayments", id));
                toast.success("Payment record deleted");
                loadData();
            } catch (error) {
                toast.error("Error deleting record");
            }
        }
    };

    const handleSave = async () => {
        if (!form.staffId) return toast.error("Please select a staff member");

        if (!editMode) {
            const alreadyPaid = payments.some(p =>
                p.staffId === form.staffId &&
                Number(p.month) === Number(form.month) &&
                Number(p.year) === Number(form.year)
            );
            if (alreadyPaid) {
                return toast.error(`Salary already processed for ${months[form.month - 1]} ${form.year}`);
            }
        }

        try {
            if (editMode) {
                const { id, ...updateData } = form;
                await updateDoc(doc(db, "monthlypayments", currentId), updateData);
                toast.success("Payroll updated successfully");
            } else {
                const docRef = await addDoc(collection(db, "monthlypayments"), form);

                if (form.shouldDeductAdvance && form.advanceDeduction > 0) {
                    const monthAdvs = advances.filter(a => {
                        if (a.staffId !== form.staffId) return false;
                        const d = new Date(a.date);
                        return d.getMonth() + 1 === Number(form.month) && d.getFullYear() === Number(form.year);
                    });

                    for (let adv of monthAdvs) {
                        await updateDoc(doc(db, "staffadvances", adv.id), {
                            status: "Deducted",
                            paidInId: docRef.id
                        });
                    }
                    toast.success(`Salary processed & ₹${form.advanceDeduction} advance settled`);
                } else {
                    toast.success("Salary processed successfully");
                }
            }
            setOpen(false);
            loadData();
        } catch (error) {
            toast.error("Database error: Could not save");
        }
    };

    // Filter array first so pagination numbers line up with the search results
    const filteredPayments = payments.filter(p => 
        staffList.find(s => s.id === p.staffId)?.name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Box sx={{ bgcolor: '#f9fafb', minHeight: '100vh' }}>
            <Toaster position="top-right" reverseOrder={false} />

            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CalendarMonth color="primary" />
                        <Typography variant="h5" fontWeight="800">Staff Payroll</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<ReceiptLong />} onClick={() => handleOpen()} sx={{ borderRadius: 2 }}>
                        Generate Salary
                    </Button>
                </Stack>

                <TextField
                    fullWidth size="small" placeholder="Search by staff name..."
                    value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                        sx: { borderRadius: 3, bgcolor: '#fff' }
                    }}
                />

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Sr No.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Staff Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Paid Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Earned</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Advance</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Net Paid</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPayments
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {staffList.find(s => s.id === row.staffId)?.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{months[row.month - 1]} {row.year}</TableCell>
                                        <TableCell>{row.payDate}</TableCell>
                                        <TableCell>₹{row.attendanceEarnings}</TableCell>
                                        <TableCell sx={{ color: row.advanceDeduction > 0 ? "error.main" : "text.secondary" }}>
                                            {row.advanceDeduction > 0 ? `-₹${row.advanceDeduction}` : "₹0"}
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight="bold" color="primary.main">₹{row.netPaid}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleOpen(row)} color="primary"><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(row.id)} color="error"><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]} 
                    component="div" 
                    count={filteredPayments.length} // Bound count dynamically to filtered items
                    rowsPerPage={rowsPerPage} 
                    page={page} 
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editMode ? "Update Payroll" : "Process Monthly Salary"}</DialogTitle>
                <DialogContent dividers>
                    <Grid className="row" spacing={2} sx={{ mt: 0.5 }}>
                        <Grid className="col-sm-12 mb-3" item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Staff Member</InputLabel>
                                <Select size="small" value={form.staffId} label="Staff Member" disabled={editMode}
                                    onChange={(e) => calculateLogic(e.target.value, form.month, form.year, form.shouldDeductAdvance)}
                                >
                                    {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.post})</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Month</InputLabel>
                                <Select size="small" value={form.month} label="Month"
                                    onChange={(e) => { calculateLogic(form.staffId, e.target.value, form.year, form.shouldDeductAdvance); }}
                                >
                                    {months.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Year</InputLabel>
                                <Select size="small" value={form.year} label="Year"
                                    onChange={(e) => { calculateLogic(form.staffId, form.month, e.target.value, form.shouldDeductAdvance); }}
                                >
                                    {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={12}>
                            <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 3, border: '1px solid #d0e3ff' }}>
                                <Stack spacing={1}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">Earned Salary (3 Absences Free)</Typography>
                                        <Typography variant="body2" fontWeight="bold">₹{form.attendanceEarnings}</Typography>
                                    </Box>

                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <FormControlLabel
                                            control={<Switch size="small" checked={form.shouldDeductAdvance}
                                                onChange={(e) => calculateLogic(form.staffId, form.month, form.year, e.target.checked)} />}
                                            label={<Typography variant="caption">Deduct Pending Advance</Typography>}
                                        />
                                        <Typography variant="body2" fontWeight="bold" color={form.shouldDeductAdvance ? "error.main" : "text.secondary"}>
                                            -₹{form.advanceDeduction}
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="subtitle1" fontWeight="bold">Net Payable</Typography>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">₹{form.netPaid}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>

                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <TextField type="date" size="small" label="Payment Date" fullWidth InputLabelProps={{ shrink: true }} value={form.payDate}
                                onChange={(e) => setForm({ ...form, payDate: e.target.value })} />
                        </Grid>
                        <Grid className="col-sm-6 mb-3" item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Pay Method</InputLabel>
                                <Select size="small" value={form.method} label="Pay Method" onChange={(e) => setForm({ ...form, method: e.target.value })}>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Online">UPI / Online</MenuItem>
                                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disableElevation sx={{ px: 4, borderRadius: 2 }}>
                        {editMode ? "Update" : "Finish & Pay"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Monthlypayment;